import { useState, useEffect } from "react";
import { 
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@/components";
import type { ProjectResource } from "@/features/Projects/models/project.types";
import type { ResourceEditorProps } from "../resourcesPhase.types";
import { 
  RESOURCE_TYPE_CONFIG, 
  generateResourceId, 
  calculateResourceCost,
  DEFAULT_HOURLY_RATES 
} from "../resourcesPhase.definitions";

export function ResourceEditor({ resource, onSave, onCancel }: ResourceEditorProps) {
  const [formData, setFormData] = useState({
    id: resource?.id || generateResourceId(),
    name: resource?.name || "",
    type: resource?.type || "developer",
    hoursEstimated: resource?.hoursEstimated || 0,
    hourlyRate: resource?.hourlyRate || DEFAULT_HOURLY_RATES.developer.pleno,
    totalCost: resource?.totalCost || 0,
  });

  const [seniority, setSeniority] = useState<keyof typeof DEFAULT_HOURLY_RATES.developer>("pleno");

  // Atualizar valor total quando horas ou taxa mudam
  useEffect(() => {
    const newTotalCost = calculateResourceCost(formData.hoursEstimated, formData.hourlyRate);
    setFormData(prev => ({ ...prev, totalCost: newTotalCost }));
  }, [formData.hoursEstimated, formData.hourlyRate]);

  // Atualizar taxa quando tipo ou senioridade mudam
  useEffect(() => {
    const typeRates = DEFAULT_HOURLY_RATES[formData.type as keyof typeof DEFAULT_HOURLY_RATES];
    if (typeRates) {
      const newRate = typeRates[seniority];
      setFormData(prev => ({ ...prev, hourlyRate: newRate }));
    }
  }, [formData.type, seniority]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSeniorityChange = (newSeniority: keyof typeof DEFAULT_HOURLY_RATES.developer) => {
    setSeniority(newSeniority);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      return;
    }

    onSave(formData as ProjectResource);
  };

  const isEditing = resource !== null;

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">
          {isEditing ? "Editar Recurso" : "Novo Recurso"}
        </h3>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Nome */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome/Função
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Ex: Desenvolvedor Full-stack Sênior"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Tipo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tipo
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange("type", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {Object.entries(RESOURCE_TYPE_CONFIG).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label}
                </option>
              ))}
            </select>
          </div>

          {/* Senioridade */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Senioridade
            </label>
            <select
              value={seniority}
              onChange={(e) => handleSeniorityChange(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="junior">Júnior</option>
              <option value="pleno">Pleno</option>
              <option value="senior">Sênior</option>
              <option value="tech_lead">Tech Lead</option>
            </select>
          </div>

          {/* Horas Estimadas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Horas Estimadas
            </label>
            <input
              type="number"
              value={formData.hoursEstimated}
              onChange={(e) => handleInputChange("hoursEstimated", parseInt(e.target.value) || 0)}
              min="0"
              step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Taxa por Hora */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Taxa por Hora (R$)
            </label>
            <input
              type="number"
              value={formData.hourlyRate}
              onChange={(e) => handleInputChange("hourlyRate", parseFloat(e.target.value) || 0)}
              min="0"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Custo Total */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Custo Total (R$)
            </label>
            <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900 font-medium">
              {formData.totalCost.toLocaleString('pt-BR')}
            </div>
          </div>
        </div>

        {/* Resource Type Description */}
        {RESOURCE_TYPE_CONFIG[formData.type] && (
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-600">
              <strong>Responsabilidades:</strong> {RESOURCE_TYPE_CONFIG[formData.type].description}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            leftIcon={<CheckIcon className="h-4 w-4" />}
          >
            {isEditing ? "Atualizar Recurso" : "Criar Recurso"}
          </Button>
        </div>
      </form>
    </div>
  );
}